import * as React from "react";
import { authAPI } from "../auth";
import { useLatestQuery } from "../useLatestQuery";
import { metadataAPI } from "./index";
import {
  sortAndPartitionProfilesByRecentConcerns,
  type ProfilesSortedAndPartitionedByRecentConcerns,
} from "./sortAndPartitionProfilesByRecentConcerns";

export type { ProfilesSortedAndPartitionedByRecentConcerns };

export type UseProfilesSortedAndPartitionedByRecentConcernsResult =
  ProfilesSortedAndPartitionedByRecentConcerns & {
    isReady: boolean;
  };

export interface UseProfilesSortedAndPartitionedByRecentConcernsOptions {
  /**
   * When false, getUserMetadata is not fetched and partitioning runs without
   * last-concern recency once myProfiles has loaded.
   */
  fetchUserMetadata?: boolean;
}

/**
 * Loads myProfiles and (by default) user metadata, then sorts and partitions like the profile menu.
 * `isReady` is false until every enabled request has finished.
 */
export function useProfilesSortedAndPartitionedByRecentConcerns(
  options?: UseProfilesSortedAndPartitionedByRecentConcernsOptions
): UseProfilesSortedAndPartitionedByRecentConcernsResult {
  const fetchUserMetadata = options?.fetchUserMetadata !== false;

  const { data: myProfiles, isFetched: profilesFetched } = useLatestQuery(
    ["authAPI.myProfiles"],
    () => authAPI.myProfiles({})
  );

  const { data: userMetadata, isFetched: metadataFetched } = useLatestQuery(
    ["metadataAPI.getUserMetadata"],
    () => metadataAPI.getUserMetadata({}),
    { enabled: fetchUserMetadata }
  );

  const metadataReady = !fetchUserMetadata || metadataFetched;
  const isReady = profilesFetched && metadataReady;

  return React.useMemo(() => {
    const empty: UseProfilesSortedAndPartitionedByRecentConcernsResult = {
      sortedProfiles: [],
      recentProfiles: [],
      otherProfiles: [],
      isReady,
    };

    if (!isReady) {
      return empty;
    }

    const partitioned = sortAndPartitionProfilesByRecentConcerns(
      myProfiles?.result,
      fetchUserMetadata ? userMetadata?.item?.lastConcernIds : undefined
    );

    return { ...partitioned, isReady: true };
  }, [
    isReady,
    myProfiles?.result,
    fetchUserMetadata,
    userMetadata?.item?.lastConcernIds,
  ]);
}
